/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import moment from "moment-timezone";

/**
 * Convert Date to Moment
 * @param {Date} date date
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @return{Moment} Moment
 */
export function toMoment(date: Date, timezone?: string): moment.Moment {
  return timezone ? moment.tz(date, timezone) : moment(date);
}

/**
 * Merge date and time.
 * @param{date} date date
 * @param{date} time time
 * @return{date} Merged result
 */
export function mergeDateAndTime(date: Date, time: Date): Date {
  const mergedDate = new Date(date);
  mergedDate.setHours(
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds()
  );
  return mergedDate;
}

/**
 * Format date to string
 * @param {Date} date date
 * @param {string} format format.If omitted, default format will used.
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @return {string} formatted string
 */
export const formatDate: (
  date: Date,
  format?: string,
  timezone?: string | undefined
) => string = (date, format?, timezone?) => {
  const dateWithTz = toMoment(date, timezone);
  return dateWithTz.format(format);
};

/**
 * Check whether given date is today
 * @param {Date} date date
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @param {string} granularity Granularity for comparing.If omitted, It to be "day".
 * @return {boolean} true: Today / false: Not today
 */
export function isToday(
  date: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
): boolean {
  const dateWithTz = toMoment(date, timezone);
  const todayWithTz = toMoment(new Date(), timezone);
  return dateWithTz.isSame(todayWithTz, granularity ?? "day");
}

/**
 * Check whether given date is future date
 * @param {Date} date date
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @param {string} granularity Granularity for comparing.If omitted, It to be "minute".
 * @return {boolean} true: Future / false: Past
 */
export function isFuture(
  date: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
): boolean {
  const dateWithTz = toMoment(date, timezone);
  const todayWithTz = toMoment(new Date(), timezone);
  console.debug(`dateWithTz: ${dateWithTz}`);
  console.debug(`todayWithTz: ${todayWithTz}`);
  return dateWithTz.isSameOrAfter(todayWithTz, granularity ?? "minute");
}

/**
 * Check whether given date is past date
 * @param {Date} date date
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @param {string} granularity Granularity for comparing.If omitted, It to be "minute".
 * @return {boolean} true: Future / false: Past
 */
export function isPast(
  date: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
): boolean {
  const dateWithTz = toMoment(date, timezone);
  const todayWithTz = toMoment(new Date(), timezone);
  return dateWithTz.isBefore(todayWithTz, granularity ?? "minute");
}

/**
 * Compare 2 dates.
 *
 * @export
 * @param {Date} date1 Date(1)
 * @param {Date} date2 Date(2)
 * @param {?string} [timezone] Time zone name.If omitted, local time zone will used.
 * @param {?moment.unitOfTime.StartOf} [granularity] Granularity for comparing.If omitted, It to be "minute".
 * @return {number} 0: same / -1: date1 is past / 1: date1 is future
 */
export function compareDateTime(
  date1: Date,
  date2: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
): number {
  const date1WithTz = toMoment(date1, timezone);
  const date2WithTz = toMoment(date2, timezone);
  if (date1WithTz.isSame(date2WithTz, granularity ?? "minute")) {
    return 0;
  }
  return date1WithTz.isBefore(date2WithTz, granularity ?? "minute") ? -1 : 1;
}

/**
 * Check whether given date is in range of start and end date
 * @param {Date} date date
 * @param {Date} startDate Range start date
 * @param {Date} endDate Range end date
 * @param {string} timezone Time zone name.If omitted, local time zone will used.
 * @param {string} granularity Granularity for comparing.If omitted, It to be "minute".
 * @return {boolean} true: In range / false: Out of range
 */
export function isInRange(
  date: Date,
  startDate: Date,
  endDate: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
) {
  const dateWithTz = toMoment(date, timezone);
  const startDateWithTz = toMoment(startDate, timezone);
  const endDateWithTz = toMoment(endDate, timezone);
  return (
    dateWithTz.isSameOrAfter(startDateWithTz, granularity ?? "minute") &&
    dateWithTz.isSameOrBefore(endDateWithTz, granularity ?? "minute")
  );
}

/**
 * Check whether given date is in a week
 * @param {Date} date date
 * @param {?string} timezone Time zone name.If omitted, local time zone will used.
 * @param {?string} granularity Granularity for comparing.If omitted, It to be "minute".
 * @return {boolean} true: in a week / false: not in a week
 */
export const isInAWeek = (
  date: Date,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
) => {
  // Today
  const rangeStart = new Date();
  // A week after
  const rangeEnd = new Date(rangeStart);
  rangeEnd.setDate(rangeEnd.getDate() + 7);

  // Check if input date is in a week
  return isInRange(date, rangeStart, rangeEnd, timezone, granularity ?? "day");
};

/**
 * Get elapsed time from given time to now.
 * @param {Date} from Basis time
 * @return {number} Elapsed time(ms)
 */
export function getElapsedTime(from: Date): number {
  const fromMoment = toMoment(from);
  const nowMoment = toMoment(new Date());
  return nowMoment.diff(fromMoment, "millisecond");
}

/**
 * Description placeholder
 *
 * @export
 * @param {(Date | undefined)} from Datetime from
 * @param {(Date | undefined)} to Datetime to
 * @param {string} timezone Timezone
 * @param {?string} granularity Granularity for comparing.If omitted, It to be "minute".
 * @return {boolean[]} [0]: result for from / [1]: result for to
 */
export function validateFromTo(
  from: Date | undefined,
  to: Date | undefined,
  timezone?: string,
  granularity?: moment.unitOfTime.StartOf
): boolean[] {
  const result = [true, true];
  if (
    from === undefined ||
    !isInAWeek(from, timezone, granularity ?? "minute")
  ) {
    result[0] = false;
  }
  if (to === undefined || !isInAWeek(to, timezone, granularity ?? "minute")) {
    result[1] = false;
  }
  if (
    from !== undefined &&
    to !== undefined &&
    compareDateTime(from, to, timezone, granularity) > 0
  ) {
    result[0] = false;
    result[1] = false;
  }
  return result;
}
